import React from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import globalStyles from "../../../global";
import {
  getResponsiveHeight,
  getResponsiveSize,
} from "../../../utils/responsive";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { appInfoData } from "../config/appInfoData";

const AppInfo = ({ countryCode = "uae" }) => {
  const { t, i18n } = useTranslation();
  const config = appInfoData[countryCode] || appInfoData.uae;
  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL;
  const data = config;
  const isRTL = i18n.language === "ar";

  const styles = {
    mainContainer: {
      width: "100%",
      // padding: { xs: "40px 16px", md: "60px 24px" },
      py: 8,
      px: { xs: 2, md: 0 },
    },
    contentWrapper: {
      maxWidth: "1200px",
      margin: "0 auto",
      display: "flex",
      flexDirection: { xs: "column", md: isRTL ? "row-reverse" : "row" },
      gap: 4,
      alignItems: "center",
    },
    leftSection: {
      flex: 1,
      // paddingRight: isRTL ? { xs: 0, md: "40px" } : { xs: 0, md: 0 },
      // paddingLeft: isRTL ? { xs: 0, md: 0 } : { xs: 0, md: "40px" },
    },
    title: {
      color: globalStyles.colors.army_green,
      fontSize: {
        md: "40px",
        sm: "28px",
        xs: "24px",
      },
      fontWeight: 600,
      textAlign: isRTL ? "right" : "left",
    },
    titleSection: {
      marginBottom: "16px",
    },
    description: {
      color: globalStyles.colors.uclean_medium_gray,
      fontSize: {
        sm: "20px",
        xs: "16px",
      },
      marginBottom: "24px",
      lineHeight: "auto",
      letterSpacing: "1%",
      fontFamily: globalStyles.fonts.primary,
      textAlign: isRTL ? "right" : "left",
    },
    verticalDivider: {
      width: "1.5px",
      background: "#E5E5E5",
      height: { xs: "65px", md: "50px" },
      margin: { xs: "0 18px", md: "0 28px" },
      display: "inline-block",
    },
    checklistContainer: {
      display: "flex",
      flexDirection: "column",
      gap: 1,
      marginBottom: "32px",
    },
    checklistItem: {
      display: "flex",
      alignItems: "center",
      flexDirection: isRTL ? "row-reverse" : "row",
      justifyContent: isRTL ? "flex-start" : "flex-start",
    },
    checkIcon: {
      color: globalStyles.colors.army_green,
      marginRight: isRTL ? 0 : "8px",
      marginLeft: isRTL ? "8px" : 0,
    },
    checklistText: {
      color: globalStyles.colors.text,
      fontSize: {
        sm: "20px",
        xs: "16px",
      },
      fontFamily: globalStyles.fonts.primary,
      textAlign: isRTL ? "right" : "left",
    },
    downloadSection: {
      display: "flex",
      alignItems: "center",
      gap: { xs: "12px", sm: "32px" },
      flexWrap: "nowrap",
      flexDirection: isRTL ? "row-reverse" : "row",
      // justifyContent: isRTL ? "space-around" : "space-around",
    },
    qrCodeContainer: {
      textAlign: "center",
      flexShrink: 0,
    },
    qrCode: {
      width: { xs: getResponsiveSize(80), md: getResponsiveSize(120) },
      height: { xs: getResponsiveHeight(80), md: getResponsiveHeight(120) },
      marginBottom: "8px",
      // Remove RTL transform
    },
    qrText: {
      color: globalStyles.colors.text,
      fontSize: { xs: getResponsiveSize(10), md: getResponsiveSize(12) },
      fontFamily: globalStyles.fonts.primary,
    },
    storeButtons: {
      display: "flex",
      gap: { xs: "8px", sm: "16px" },
      flexDirection: { xs: "column", sm: isRTL ? "row-reverse" : "row" }, // Column on mobile, row on larger screens
      flexShrink: 0,
    },
    storeButton: {
      width: { xs: "", sm: "128px" },
      cursor: "pointer",
      transition: "transform 0.2s ease",
      // Remove RTL transform
      "&:hover": {
        transform: "translateY(-2px)",
      },
    },
    appPreview: {
      width: "100%",
      maxWidth: { xs: "120px", md: "500px" },
      height: "auto",
      // Remove RTL transform
    },
    rightSection: {
      // flex: { xs: 0, md: 1 }, // Don't take flex space on mobile
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
  };

  return (
    <Box sx={styles.mainContainer}>
      <Box sx={styles.contentWrapper}>
        <Box sx={styles.leftSection}>
          <Box sx={styles.titleSection}>
            {" "}
            <Typography variant="h1_gothic" sx={styles.title}>
              {t("appInfoSr.heading")}
            </Typography>
          </Box>

          <Typography variant="body1" sx={styles.description}>
            {t("appInfoSr.description")}
          </Typography>

          <Box sx={styles.checklistContainer}>
            {t("appInfoSr.checklistItems", { returnObjects: true }).map(
              (item, index) => (
                <Box key={index} sx={styles.checklistItem}>
                  <CheckCircleOutlineIcon sx={styles.checkIcon} />
                  <Typography variant="body2" sx={styles.checklistText}>
                    {item}
                  </Typography>
                </Box>
              )
            )}
          </Box>

          <Box sx={styles.downloadSection}>
            <Box sx={styles.qrCodeContainer}>
              <Box
                component="img"
                src={`${imageBaseUrl}${data.qrCodeImage}`}
                alt={t("appInfo.qrCodeText")}
                sx={styles.qrCode}
              />
              <Typography variant="caption" sx={styles.qrText}>
                {t("appInfo.qrCodeText")}
              </Typography>
            </Box>
            <Box sx={styles.verticalDivider}></Box>

            <Box sx={styles.storeButtons}>
              <Box
                component="img"
                src={`${imageBaseUrl}${data.playStoreImage}`}
                alt="Play Store"
                sx={styles.storeButton}
                onClick={() =>
                  window.open(
                    "https://play.google.com/store/apps/details?id=your.app.id"
                  )
                }
              />
              <Box
                component="img"
                src={`${imageBaseUrl}${data.appStoreImage}`}
                alt="App Store"
                sx={styles.storeButton}
                onClick={() =>
                  window.open("https://apps.apple.com/app/your.app.id")
                }
              />
            </Box>

            {/* Mobile app preview in same row on mobile */}
            <Box sx={{ display: { xs: "block", md: "none" } }}>
              <Box
                component="img"
                src={`${imageBaseUrl}${data.appInfoImage}`}
                alt="App Preview"
                sx={styles.appPreview}
              />
            </Box>
          </Box>
        </Box>

        {/* Mobile app preview only on desktop */}
        <Box
          sx={{ ...styles.rightSection, display: { xs: "none", md: "flex" } }}
        >
          <Box
            component="img"
            src={`${imageBaseUrl}${data.appInfoImage}`}
            alt="App Preview"
            sx={styles.appPreview}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AppInfo;
